import { Form } from "antd"
import { DatePicker, Group, Legend, notification, SelectInput, TextInput } from "components/common"
import { useLazyGetCheckNationalIdQuery, useLazyGetCitiesQuery } from "features/shared/api"
import { memo, useCallback, useMemo } from "react"
import { getCountryList } from "utils"
import { debounce } from "lodash"
import { t } from "i18next"
import dayjs from "dayjs"
import { DATE_FORMAT } from "constants"
import { useAddSalesContext } from "../../../addSalesContext/addSalesContext"

const ClientDetails = ({ rules }) => {
  const { addSalesApis, form, stateManager, formWatchers, client } = useAddSalesContext()
  const { project, unit } = formWatchers
  const { countries, leadSalesInfo, isFetchingCountries, nationalities } = addSalesApis

  const country = Form.useWatch("country", form)

  const [checkCnic, { data: cnicData, isError: cnicError }] = useLazyGetCheckNationalIdQuery()
  const [getCities, { data: cities, isFetching: isFetchingCities }] = useLazyGetCitiesQuery()

  const checkFieldConditions = fieldName => {
    const isFieldRequired =
      project?.[`requires${fieldName}`] ||
      stateManager?.project?.[`requires${fieldName}`] ||
      leadSalesInfo?.project?.[`requires${fieldName}`]
    const doesFieldExist = leadSalesInfo?.[`does${fieldName}Exist`]

    return isFieldRequired && !doesFieldExist
  }

  const isAnyFieldMissing = useMemo(() => {
    const requiredFields = ["CNIC", "Nationality", "DOB", "Country", "City"]
    return requiredFields.some(field => checkFieldConditions(field))
  }, [project, leadSalesInfo, stateManager?.project])

  const debounceCnicValidator = useCallback(
    debounce(value => {
      checkCnic(value)
        .unwrap()
        .then(() => {
          form.validateFields(["nationalId"])
        })
        .catch(e => notification.error({ message: e?.error }))
    }, 500),
    []
  )

  const shouldRenderField = fieldType => {
    switch (fieldType) {
      case "nationalId":
        return checkFieldConditions("CNIC")
      case "nationality":
        return checkFieldConditions("Nationality")
      case "dateOfBirth":
        return checkFieldConditions("DOB")
      case "country":
        return checkFieldConditions("Country")
      case "city":
        return checkFieldConditions("City")
      default:
        return false
    }
  }

  return (
    <>
      {unit && project && isAnyFieldMissing && (
        <div>
          <Legend title={t("Client Details")} />
          <Group className="mbe-16" template="repeat(2, 1fr)" gap="16px 32px">
            {shouldRenderField("nationalId") && (
              <Form.Item
                required
                name="nationalId"
                label={t("National ID")}
                rules={rules.concat({
                  validator: async (_, value) => {
                    if (cnicData?.exists) {
                      return Promise.reject(t("National id already exists"))
                    } else {
                      return Promise.resolve()
                    }
                  },
                })}
              >
                <TextInput
                  placeholder={t("Enter National ID")}
                  onChange={e => {
                    !!e.target?.value && debounceCnicValidator({
                      id: client?.id,
                      nationalId: e.target.value,
                    })
                  }}
                  maxLength={13}
                />
              </Form.Item>
            )}

            {shouldRenderField("nationality") && (
              <Form.Item required name="nationality" label={t("Nationality")} rules={rules}>
                <SelectInput
                  placeholder={t("Select Nationality")}
                  options={nationalities}
                  inputLoading={isFetchingCountries}
                  onChange={(value, option) => {
                    getCities(option?.id, true)
                    form.setFieldsValue({ nationality: option })
                  }}
                />
              </Form.Item>
            )}

            {shouldRenderField("dateOfBirth") && (
              <Form.Item required name="dateOfBirth" label={t("Date of Birth")} rules={rules}>
                <DatePicker
                  allowClear={false}
                  showTime={false}
                  defaultPickerValue={dayjs().subtract(18, "year")}
                  format={DATE_FORMAT}
                  onChange={value => {
                    form.setFieldsValue({ dateOfBirth: value })
                  }}
                />
              </Form.Item>
            )}

            {shouldRenderField("country") && (
              <Form.Item required name="country" label={t("Country")} rules={rules}>
                <SelectInput
                  placeholder={t("Select Country")}
                  options={getCountryList(countries)}
                  loading={isFetchingCountries}
                  onChange={(value, option) => {
                    getCities(option.id, true)
                    form.setFieldsValue({ country: option, city: null })
                  }}
                />
              </Form.Item>
            )}

            {shouldRenderField("city") && (
              <Form.Item required name="city" label={t("City")} rules={rules}>
                <SelectInput
                  placeholder={t("Select City")}
                  inputLoading={isFetchingCities}
                  disabled={!(!!country || !!leadSalesInfo?.countryId)}
                  options={cities}
                  onFocus={() => {
                    if (!!leadSalesInfo?.countryId) {
                      getCities(leadSalesInfo?.countryId, true)
                    }
                  }}
                  onChange={(value, option) => {
                    form.setFieldValue("city", option)
                  }}
                />
              </Form.Item>
            )}
          </Group>
        </div>
      )}
    </>
  )
}

export default memo(ClientDetails)
